// ============================================
// moldClaw - Payment Success Page
// ============================================

/**
 * 🧑‍💻 SWE-2: 결제 성공 페이지
 * 🎨 CX-2: 토스 결제 성공 UX 벤치마킹
 *   - 깔끔한 성공 메시지
 *   - 다음 단계 안내
 *   - 영수증 확인 링크
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

// 플랜 정보
const PLAN_INFO = {
  pro: { name: 'Pro', price: '₩19,900', features: ['기기 3대', '모든 기능'] },
  team: { name: 'Team', price: '₩32,900', features: ['기기 5대', '팀 협업', '우선 지원'] },
};

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: _user } = useAuth();
  
  const [showConfetti, setShowConfetti] = useState(true);
  
  // URL 파라미터에서 결제 정보 추출
  const tier = (searchParams.get('tier') as 'pro' | 'team') || 'pro';
  const amount = searchParams.get('amount') || PLAN_INFO[tier]?.price;
  const receiptUrl = searchParams.get('receipt');
  // subscriptionId는 향후 사용 예정
  
  const planInfo = PLAN_INFO[tier] || PLAN_INFO.pro;
  
  // 3초 후 confetti 숨기기
  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      {/* Confetti Animation (CSS) */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-10px',
                animationDelay: `${Math.random() * 2}s`,
                backgroundColor: ['#6366f1', '#8b5cf6', '#ec4899', '#10b981', '#f59e0b'][i % 5],
                width: '10px',
                height: '10px',
                borderRadius: Math.random() > 0.5 ? '50%' : '0',
              }}
            />
          ))}
        </div>
      )}
      
      <div className="max-w-md w-full">
        {/* Success Card */}
        <div className="bg-gray-800 rounded-2xl p-8 text-center shadow-xl">
          {/* Success Icon */}
          <div className="w-20 h-20 mx-auto mb-6 bg-green-500/20 rounded-full flex items-center justify-center">
            <svg
              className="w-10 h-10 text-green-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          
          {/* Title */}
          <h1 className="text-2xl font-bold text-white mb-2">
            결제가 완료되었습니다! 🎉
          </h1>
          
          <p className="text-gray-400 mb-6">
            {planInfo.name} 플랜으로 업그레이드되었습니다
          </p>
          
          {/* Plan Details */}
          <div className="bg-gray-700/50 rounded-xl p-4 mb-6 text-left">
            <div className="flex justify-between items-center mb-3">
              <span className="text-gray-400">플랜</span>
              <span className="text-white font-semibold">{planInfo.name}</span>
            </div>
            <div className="flex justify-between items-center mb-3">
              <span className="text-gray-400">결제 금액</span>
              <span className="text-white font-semibold">{amount}/월</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">다음 결제일</span>
              <span className="text-white">{getNextBillingDate()}</span>
            </div>
          </div>
          
          {/* Features Unlocked */}
          <div className="mb-6 text-left">
            <p className="text-sm text-gray-400 mb-2">사용 가능한 기능:</p>
            <ul className="space-y-1">
              {planInfo.features.map((feature, i) => (
                <li key={i} className="flex items-center text-sm text-green-400">
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
          
          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full py-3 px-4 bg-space-blue hover:bg-space-blue hover:brightness-110 text-white rounded-lg font-medium transition-colors"
            >
              대시보드로 이동
            </button>
            
            <Link
              to="/download"
              className="block w-full py-3 px-4 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
            >
              앱 다운로드
            </Link>
            
            {receiptUrl && (
              <a
                href={receiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm text-space-blue-light hover:text-space-blue-light transition-colors"
              >
                영수증 확인 →
              </a>
            )}
          </div>
        </div>
        
        {/* Help Text */}
        <p className="text-center text-gray-500 text-sm mt-6">
          결제 관련 문의: support@moldclaw.com
        </p>
      </div>
      
      {/* Confetti CSS */}
      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(-10px) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti-fall 3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}

function getNextBillingDate(): string {
  const date = new Date();
  date.setMonth(date.getMonth() + 1);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
